import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { restaurantConfig } from '../config/restaurantConfig';
// Import Swiper for mobile ratings carousel
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/autoplay';

interface HeroProps {
  language: string;
}

export const Hero = ({ language }: HeroProps) => {
  const { tagline } = restaurantConfig;
  const googleMapsUrl = "https://maps.google.com"; // Update with the real Google Maps URL for No Patio
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const scrollToDirections = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    
    // Scroll to the contact section
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      // Calculate position to scroll to (deeper into the contact section)
      const directionsButton = document.getElementById('directions-button');
      
      if (directionsButton) {
        // Get the button's position
        const buttonPosition = directionsButton.getBoundingClientRect();
        // Get the button's position relative to the page
        const absoluteButtonPosition = buttonPosition.top + window.pageYOffset;
        // Scroll directly to the button position with a small offset to ensure it's visible
        window.scrollTo({
          top: absoluteButtonPosition - 220, // Increased offset to scroll less far down
          behavior: 'smooth'
        });
        
        // After scrolling completes, animate the directions button
        setTimeout(() => {
          // Add animation classes
          directionsButton.classList.add('pulse-animation');
          directionsButton.classList.add('attention-animation');
          
          // Remove animation classes after animation is complete
          setTimeout(() => {
            directionsButton.classList.remove('pulse-animation');
            directionsButton.classList.remove('attention-animation');
          }, 3000); // Animation duration
        }, 1000); // Wait for scroll to complete
      } else {
        // Fallback if button isn't found
        contactSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, []);

  // Rating badges data for reuse - filter to only include Google and TheFork
  const ratingBadges = [
    {
      name: "Google",
      rating: "4.7",
      url: "https://www.google.com/search?client=firefox-b-d&sca_esv=6f5fcc8f272f4316&sxsrf=AHTn8zqr3iRZFdtGWWV7lFclR4fq4rlrxQ:1744478970684&q=no+patio+restaurant+lagos&si=APYL9bs7Hg2KMLB-4tSoTdxuOx8BdRvHbByC_AuVpNyh0x2KzQkn013pVLlQRZBZOV4POkGnTvIXngJg0JGH8qZMkxz5qd9yfW_6oLc36A6jGT8gxwy1SDI%3D&uds=ABqPDvxpkLLAJEhdE2OSBlj52v8qebFNkHrP9re03JmAptqp8YJzq11SNJGErd5hcrRg09f4qPU5XuDIkgMIcSLQkBHfRzHpMsPvJeNjosD3mrVZeTHtDQH7WPJhTgXAZGJxUsSEjhJG&sa=X&ved=2ahUKEwino_bKgtOMAxVSVqQEHdUkKCoQ3PALegQIGRAE",
      iconClass: "fab fa-google",
      color: "var(--color-pink)"
    },
    {
      name: "TheFork",
      rating: "9.6/10",
      url: "https://www.thefork.pt/restaurante/restaurante-no-patio-r827550",
      iconClass: "fas fa-utensils",
      color: "#f9a702"
    }
  ];

  // Render rating badge component
  const RatingBadge = ({ badge }: { badge: typeof ratingBadges[0] }) => (
    <a 
      href={badge.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`rating-badge cursor-pointer transition-all duration-300 ease-in-out hover:transform hover:scale-105 fade-in-animation bg-white rounded-xl shadow-md ${isMobile ? 'py-1 px-2' : 'py-2 px-4'}`}
    >
      <div className="rating-stars flex items-center justify-center gap-1">
        <div className={`flex gap-1 ${isMobile ? 'text-base' : 'text-lg'}`} style={{ color: badge.color }}>
          <i className="fas fa-star"></i>
          <i className="fas fa-star"></i>
          <i className="fas fa-star"></i>
          <i className="fas fa-star"></i>
          <i className="fas fa-star-half-alt"></i>
        </div>
        <span className={`rating-score !text-black font-extrabold ml-1 ${isMobile ? 'text-lg' : 'text-xl'}`}>{badge.rating}</span>
      </div>
      <div className="rating-source text-black font-bold mt-1 text-center text-sm">
        <i className={`${badge.iconClass} !text-black`}></i> 
        <span className="ml-1 !text-black">{badge.name}</span>
      </div>
    </a>
  );

  return (
    <section className="hero-section relative min-h-screen w-full overflow-hidden flex flex-col items-center justify-start">
      <div className="absolute inset-0 z-0">
        {/* Use responsive images with WebP format for better performance */}
        <picture>
          {/* Mobile version - WebP format for modern browsers */}
          <source 
            media="(max-width: 768px)" 
            srcSet="/images/Hero Image/hero-mobile.webp"
            type="image/webp"
          />
          {/* Mobile version - PNG fallback */}
          <source 
            media="(max-width: 768px)" 
            srcSet="/images/Hero Image/hero-mobile.png"
            type="image/png"
          />
          {/* Desktop version - WebP format for modern browsers */}
          <source 
            srcSet="/images/Hero Image/hero.webp"
            type="image/webp"
          />
          {/* Desktop version - PNG fallback */}
          <img 
            src="/images/Hero Image/hero.png" 
            alt="No Patio Restaurant Interior" 
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            onLoad={(e) => {
              // Fade in the image once loaded
              const target = e.target as HTMLImageElement;
              target.style.opacity = '1';
              
              // Add loaded class to hero section for animations
              setTimeout(() => {
                const heroSection = document.querySelector('.hero-section');
                if (heroSection) {
                  heroSection.classList.add('loaded');
                }
              }, 100);
            }}
            onError={(e) => {
              console.error('Failed to load hero image:', e);
              const target = e.target as HTMLImageElement;
              target.onerror = null; // Prevent infinite loop if fallback also fails
              target.src = '/images/logo.png'; // Fallback to logo if image fails to load
            }}
            style={{
              objectPosition: 'center center',
              opacity: '0',
              transition: 'opacity 0.3s ease-in-out'
            }}
          />
        </picture>
        {/* Garden overlay with subtle leaf patterns - increased opacity */}
        <div className="absolute inset-0 bg-black opacity-40"></div>
      </div>
      
      {/* First birds group (individual) */}
      <div className="bird-container bird-container-one">
        <div className="bird bird-one"></div>
      </div>
      <div className="bird-container bird-container-two">
        <div className="bird bird-two"></div>
      </div>
      <div className="bird-container bird-container-three">
        <div className="bird bird-three"></div>
      </div>
      <div className="bird-container bird-container-four">
        <div className="bird bird-four"></div>
      </div>
      
      {/* Second birds group (flock) */}
      <div className="flock-container flock-one">
        <div className="bird flock-bird-1"></div>
        <div className="bird flock-bird-2"></div>
        <div className="bird flock-bird-3"></div>
        <div className="bird flock-bird-4"></div>
        <div className="bird flock-bird-5"></div>
      </div>

      {/* Third group with different pattern */}
      <div className="bird-container bird-container-alt">
        <div className="bird bird-alt"></div>
      </div>
      
      {/* Fixed width container for consistent centering */}
      <div className="hero-content w-full max-w-7xl mx-auto flex flex-col items-center justify-center px-4 relative z-10">
        {/* Logo with garden animation */}
        <div className="w-full flex justify-center" id="hero-logo">
          <img 
            src="/images/logo.png" 
            alt="No Patio Logo" 
            className="w-auto h-[12rem] sm:h-[18rem] md:h-[18rem] lg:h-[20rem] object-contain garden-animation"
          />
        </div>
      
        {/* Add the garden and bird animations CSS */}
        <style dangerouslySetInnerHTML={{
          __html: `
              /* Garden Float Animation */
              @keyframes gardenFloat {
                0% {
                  transform: translateY(0) rotate(0deg);
                }
                33% {
                  transform: translateY(-10px) rotate(2deg);
                }
                66% {
                  transform: translateY(5px) rotate(-1deg);
                }
                100% {
                  transform: translateY(0) rotate(0deg);
                }
              }
              
              .garden-animation {
                animation: gardenFloat 6s ease-in-out infinite;
                transform-origin: center;
              }

              /* Proper vertical centering */
              .hero-section {
                display: flex;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
                height: 100vh;
                width: 100%;
                position: relative;
              }
              
              .hero-content {
                /* Shift up from the exact center */
                transform: translateY(-12vh);
              }
              
              /* Logo positioning based on screen size */
              #hero-logo {
                margin-bottom: 2.5rem;
                width: 100%;
                display: flex;
                justify-content: center;
              }
              
              /* Mobile adjustments */
              @media screen and (max-width: 767px) {
                .hero-content {
                  /* Slightly less shift on mobile */
                  transform: translateY(-8vh);
                }
                
                #hero-logo {
                  margin-bottom: 2rem;
                }
              }
              
              /* Tablet adjustments */
              @media screen and (min-width: 768px) and (max-width: 1023px) {
                .hero-section {
                  --navbar-height: 70px;
                  min-height: 100vh;
                  height: 100vh;
                }
                
                .hero-content {
                  margin-top: -6vh;
                  top: calc(var(--navbar-height) / 3);
                }
              }
              
              /* Desktop and larger screens */
              @media screen and (min-width: 1024px) {
                .hero-section {
                  --navbar-height: 80px;
                  min-height: 100vh;
                  height: 100vh;
                }
                
                .hero-content {
                  margin-top: -2vh;
                  top: calc(var(--navbar-height) / 3);
                }
              }
              
              /* Mac-specific adjustments */
              @media screen and (-webkit-min-device-pixel-ratio: 2) {
                .hero-section {
                  --navbar-height: 80px;
                }
                
                .hero-content {
                  margin-top: -2vh;
                  top: calc(var(--navbar-height) / 3);
                }
              }
            `
        }} />
       
        {/* Add bird animations CSS */}
        <style dangerouslySetInnerHTML={{
          __html: `
            /* Bird Animation */
            .bird {
              background-image: url('https://s3-us-west-2.amazonaws.com/s.cdpn.io/174479/bird-cells-new.svg');
              filter: brightness(0) saturate(100%) invert(32%) sepia(15%) saturate(2867%) hue-rotate(169deg) brightness(114%) contrast(100%);
              background-size: auto 100%;
              width: 88px;
              height: 125px;
              will-change: background-position;
              animation-name: fly-cycle;
              animation-timing-function: steps(10);
              animation-iteration-count: infinite;
              opacity: 0.8;
            }
            
            /* Individual Birds */
            .bird-one {
              animation-duration: 1s;
              animation-delay: -0.5s;
            }
            
            .bird-two {
              animation-duration: 0.9s;
              animation-delay: -0.75s;
            }
            
            .bird-three {
              animation-duration: 1.25s;
              animation-delay: -0.25s;
            }
            
            .bird-four {
              animation-duration: 1.1s;
              animation-delay: -0.5s;
            }
            
            .bird-alt {
              animation-duration: 1.3s;
              animation-delay: -0.3s;
            }

            /* Flock birds */
            .flock-bird-1 {
              animation-duration: 0.7s;
              animation-delay: -0.2s;
              transform: scale(0.8);
              position: absolute;
              left: 0;
              top: 0;
            }
            
            .flock-bird-2 {
              animation-duration: 0.65s;
              animation-delay: -0.3s;
              transform: scale(0.9);
              position: absolute;
              left: 30px;
              top: -25px;
            }
            
            .flock-bird-3 {
              animation-duration: 0.75s;
              animation-delay: -0.1s;
              transform: scale(0.7);
              position: absolute;
              left: 75px;
              top: 10px;
            }
            
            .flock-bird-4 {
              animation-duration: 0.6s;
              animation-delay: -0.4s;
              transform: scale(0.85);
              position: absolute;
              left: 120px;
              top: -15px;
            }
            
            .flock-bird-5 {
              animation-duration: 0.7s;
              animation-delay: -0.25s;
              transform: scale(0.75);
              position: absolute;
              left: 160px;
              top: 5px;
            }

            /* Container animations */
            .bird-container {
              position: absolute;
              top: 10%;
              left: -3%;
              transform: scale(0) translateX(-10vw);
              will-change: transform;
              animation-name: fly-right-one;
              animation-timing-function: linear;
              animation-iteration-count: infinite;
              z-index: 1;
            }
            
            .bird-container-one {
              animation-duration: 15s;
              animation-delay: 0s;
              top: 15%;
            }
            
            .bird-container-two {
              animation-duration: 16s;
              animation-delay: 8s;
              top: 30%;
            }
            
            .bird-container-three {
              animation-duration: 14.6s;
              animation-delay: 20.5s;
              top: 22%;
            }
            
            .bird-container-four {
              animation-duration: 16s;
              animation-delay: 30.25s;
              top: 40%;
            }
            
            .bird-container-alt {
              animation-duration: 18s;
              animation-delay: 40s;
              top: 25%;
              animation-name: fly-right-curved;
            }
            
            /* Flock container */
            .flock-container {
              position: absolute;
              top: 18%;
              left: -3%;
              transform: scale(0) translateX(-10vw);
              will-change: transform;
              animation-name: fly-flock;
              animation-timing-function: linear;
              animation-iteration-count: infinite;
              width: 300px;
              height: 150px;
              position: relative;
              z-index: 1;
            }
            
            .flock-one {
              animation-duration: 12s;
              animation-delay: 15s;
            }

            @keyframes fly-cycle {
              100% {
                background-position: -900px 0;
              }
            }
            
            /* Standard flight pattern */
            @keyframes fly-right-one {
              0% {
                transform: scale(0.3) translateX(-10vw);
              }
              
              10% {
                transform: translateY(2vh) translateX(10vw) scale(0.4);
              }
              
              20% {
                transform: translateY(0vh) translateX(30vw) scale(0.5);
              }
              
              30% {
                transform: translateY(4vh) translateX(50vw) scale(0.6);
              }
              
              40% {
                transform: translateY(2vh) translateX(70vw) scale(0.6);
              }
              
              50% {
                transform: translateY(0vh) translateX(90vw) scale(0.6);
              }
              
              60% {
                transform: translateY(0vh) translateX(110vw) scale(0.6);
              }
              
              100% {
                transform: translateY(0vh) translateX(110vw) scale(0.6);
              }
            }
            
            /* Curved flight pattern */
            @keyframes fly-right-curved {
              0% {
                transform: scale(0.3) translateX(-10vw) translateY(10vh);
              }
              
              10% {
                transform: translateY(-5vh) translateX(10vw) scale(0.4);
              }
              
              20% {
                transform: translateY(-10vh) translateX(30vw) scale(0.5);
              }
              
              30% {
                transform: translateY(-5vh) translateX(50vw) scale(0.6);
              }
              
              40% {
                transform: translateY(5vh) translateX(70vw) scale(0.6);
              }
              
              50% {
                transform: translateY(10vh) translateX(90vw) scale(0.6);
              }
              
              60% {
                transform: translateY(5vh) translateX(110vw) scale(0.6);
              }
              
            100% {
                transform: translateY(0vh) translateX(110vw) scale(0.6);
              }
            }
            
            /* Flock movement - faster than individual birds */
            @keyframes fly-flock {
              0% {
                transform: scale(0.6) translateX(-10vw) translateY(0vh);
              }
              
              15% {
                transform: translateY(-2vh) translateX(20vw) scale(0.7);
              }
              
              30% {
                transform: translateY(3vh) translateX(40vw) scale(0.8);
              }
              
              45% {
                transform: translateY(-4vh) translateX(60vw) scale(0.8);
              }
              
              60% {
                transform: translateY(2vh) translateX(80vw) scale(0.8);
              }
              
              75% {
                transform: translateY(-3vh) translateX(100vw) scale(0.8);
              }
              
              100% {
                transform: translateY(0vh) translateX(110vw) scale(0.8);
              }
            }
            
            /* Mobile-specific faster bird animations */
            @media screen and (max-width: 767px) {
              .bird-container-one {
                animation-duration: 8s !important;
              }
              
              .bird-container-two {
                animation-duration: 9s !important;
              }
              
              .bird-container-three {
                animation-duration: 7.5s !important;
              }
              
              .bird-container-four {
                animation-duration: 8.5s !important;
              }
              
              .bird-container-alt {
                animation-duration: 10s !important;
              }
              
              .flock-one {
                animation-duration: 6s !important;
              }
              
              /* Speed up individual bird wing flapping on mobile */
              .bird-one {
                animation-duration: 0.6s !important;
              }
              
              .bird-two {
                animation-duration: 0.5s !important;
              }
              
              .bird-three {
                animation-duration: 0.7s !important;
              }
              
              .bird-four {
                animation-duration: 0.6s !important;
              }
              
              .bird-alt {
                animation-duration: 0.8s !important;
              }
              
              /* Speed up flock bird wing flapping */
              .flock-bird-1 {
                animation-duration: 0.4s !important;
              }
              
              .flock-bird-2 {
                animation-duration: 0.35s !important;
              }
              
              .flock-bird-3 {
                animation-duration: 0.45s !important;
              }
              
              .flock-bird-4 {
                animation-duration: 0.3s !important;
              }
              
              .flock-bird-5 {
                animation-duration: 0.4s !important;
              }
            }
            
            /* Animation and transition utility classes */
            .rating-badge {
              transition: all 0.4s ease;
              will-change: opacity, transform;
              opacity: 1;
            }
            
            .fade-in-animation {
              animation: none !important;
              opacity: 1 !important;
            }
            
            /* Fix for mobile rating swiper */
            @media screen and (max-width: 767px) {
              .hero-ratings-swiper {
                padding-bottom: 30px !important;
                margin-top: 0 !important;
              }
              
              .hero-ratings-swiper .swiper-slide {
                display: flex !important;
                justify-content: center !important;
                align-items: center !important;
                height: auto !important;
              }
              
              .rating-badge {
                transform: scale(0.9);
                margin: 0 auto;
                max-width: 200px;
                height: auto !important;
                padding: 0.5rem 1rem !important;
              }
              
              .swiper-pagination {
                bottom: 0px !important;
                position: absolute !important;
                width: 100% !important;
                text-align: center !important;
              }
              
              .swiper-pagination-bullet {
                background: white !important;
                opacity: 0.7 !important;
                margin: 0 3px !important;
                width: 6px !important;
                height: 6px !important;
              }
              
              .swiper-pagination-bullet-active {
                background: var(--color-pink) !important;
                opacity: 1 !important;
              }
            }
          `
        }} />
       
        {/* Ratings Badges - Desktop View */}
        {!isMobile && (
          <div className="flex flex-wrap justify-center items-center gap-4 mb-10 mt-12">
            {ratingBadges.map((badge, index) => (
              <RatingBadge key={index} badge={badge} />
            ))}
          </div>
        )}
      
        {/* Ratings Badges - Mobile View with Swiper */}
        {isMobile && (
          <div className="w-full max-w-[280px] mx-auto mt-8 mb-1">
            <Swiper
              className="hero-ratings-swiper"
              modules={[Autoplay, Pagination]}
              spaceBetween={5}
              slidesPerView={1}
              pagination={{ 
                clickable: true,
                el: '.swiper-pagination'
              }}
              autoplay={{ delay: 3000, disableOnInteraction: false }}
              loop={true}
              style={{ paddingBottom: '25px' }}
            >
              {ratingBadges.map((badge, index) => (
                <SwiperSlide key={index} className="flex justify-center items-center">
                  <RatingBadge badge={badge} />
                </SwiperSlide>
              ))}
              <div className="swiper-pagination"></div>
            </Swiper>
          </div>
        )}
        
        {/* Menu Button */}
        <div className="flex flex-col sm:flex-row justify-center gap-2 mt-3 md:mt-4">
          <a
            href="#menu"
            className="inline-block bg-[var(--color-pink)] text-white px-5 py-2.5 rounded-full transition-all duration-300 hover:bg-opacity-90 hover:shadow-lg hover:-translate-y-1 font-bold"
            onClick={(e) => {
              e.preventDefault();
              const menuSection = document.getElementById('menu');
              if (menuSection) {
                menuSection.scrollIntoView({ behavior: 'smooth' });
              } else {
                // If on a different page, redirect to home page with menu section anchor
                window.location.href = '/#menu';
              }
            }}
          >
            {language === 'en' ? 'View Our Menu' : 'Ver Nosso Menu'}
          </a>
        </div>
      </div>
    </section>
  );
}; 